import React, { useCallback, useEffect, useMemo } from "react";
import AndOrConditionBox from "./AndOrConditionBox";
import { Box, Divider, Flex } from "@chakra-ui/react";
import { Icons, Typography, useCurrencyFormatter } from "@reconvert/react-ui-component";
import { getConditionText } from "@reconvert/reconvert-utils";
import { useAppSelector } from "../../../../app/hooks";
import { selectAppState } from "../../../../app/slice/appSlice";
import { APP_PLATFORM } from "../../../../config";
import { getConditionsMetaData } from "../../../../utils/conditionsMetaData";
import { ConditionType, ConditonData } from "../../../../hooks/useAndOrGrouping";
import {
  getConditionsParamsForAndOrGrouping,
  validateRequiredValueForAndOrGrouping,
} from "../../../../utils/validateConditionData";
import { Overview, selectConditionsState } from "../../../../app/slice/conditionSlice";
import { useTranslation } from "react-i18next";

const ConditionSection = ({
  condition,
  handleAndCondition,
  handleDeleteCondition,
  updateConditionData,
  allCondition,
  newlyCreatedSubConditionId,
  conditionName,
  updateConditionName,
}: {
  condition: ConditonData;
  handleAndCondition: (parentId: string, conditionId: string) => void;
  handleDeleteCondition: (conditionId: string) => void;
  updateConditionData: (condition: ConditonData) => void;
  allCondition: ConditonData[];
  newlyCreatedSubConditionId: string | null;
  conditionName: string;
  updateConditionName: (name: string) => void;
}) => {
  const appState = useAppSelector(selectAppState);
  const { t } = useTranslation();
  const { storeInfo, activePage } = appState as { storeInfo: any; activePage: any; };
  const { currencyFormatter } = useCurrencyFormatter();
  const { overview } = useAppSelector(selectConditionsState);
  const [isOpen, setIsOpen] = React.useState(false);
  const { isInValid } = validateRequiredValueForAndOrGrouping(condition);
  const [conditionParams, setConditionParams] = React.useState<any>(null);

  const formatCurrency = useCallback(
    (value: number) => {
      return currencyFormatter(value, storeInfo?.currency!);
    },
    [currencyFormatter, storeInfo?.currency],
  );
  const conditionsMetaData = useMemo(
    () => getConditionsMetaData(activePage!, storeInfo?.isShopifyPlus!, condition.condition, t),
    [activePage, storeInfo, condition.condition],
  );

  const getConditionParams = useCallback(async () => {
    const response = await getConditionsParamsForAndOrGrouping(storeInfo?.platformStoreId, activePage!, condition);

    setConditionParams(response);
  }, [storeInfo?.platformStoreId, activePage, condition]);

  useEffect(() => {
    getConditionParams();
  }, [getConditionParams]);

  useEffect(() => {
    if (
      (overview === Overview.ADD_CONDITION_VIEW && allCondition.length === 1) ||
      newlyCreatedSubConditionId === condition?.id
    ) {
      setIsOpen(true);
    }
  }, [overview, newlyCreatedSubConditionId, condition?.id, allCondition.length]);

  const getParentId = (conditionId: string) => {
    const parentCondition = allCondition.find((c: ConditonData) => c.id === conditionId);

    if (parentCondition?.conditionType === ConditionType.OR) {
      return parentCondition?.id;
    }

    return parentCondition?.parentId || conditionId;
  };

  const onDeleteCondition = useCallback(() => {
    handleDeleteCondition(condition?.id);
  }, [handleDeleteCondition, condition?.id]);

  return (
    <Box>
      
        <Flex gap={3}>
          <Divider orientation="vertical" height={"auto"} borderLeftWidth={"2px"} />
          <Box w={"100%"}>
            <Flex justifyContent={"space-between"}>
              <Typography 
                variant="body"
                sx={{
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  whiteSpace: "normal",
                }}
              >
                {" "}
                {conditionParams ? (
                  <span
                    dangerouslySetInnerHTML={{
                      __html:
                        getConditionText(conditionParams, formatCurrency, APP_PLATFORM as any, {
                          withAnnotation: true,
                          showSpecificProductNames: false,
                          showEmptyValueMessage: true,
                          showEmptyValueMessageWithAnnotation: true,
                        }) || "",
                    }}
                  ></span>
                ) : (
                  <span>Loading...</span>
                )}
              </Typography>
              <Flex>
                {isOpen ? (
                  <Icons.CaretUp
                    cursor="pointer"
                    height="16px"
                    width="16px"
                    color="neutrals.700"
                    onClick={() => {
                      !isInValid && setIsOpen(!isOpen);
                    }}
                  />
                ) : (
                  <Icons.ChevronDown
                    cursor="pointer"
                    height="16px"
                    width="16px"
                    color="neutrals.700"
                    onClick={() => {
                      setIsOpen(!isOpen);
                    }}
                  />
                )}
              </Flex>
            </Flex>
            <Flex
              gap={2}
              mt={4}
              cursor={"pointer"}
              width={"fit-content"}
              justifyContent="center"
              align="center"
              border="1px solid #363C45"
              height="min-content"
              borderRadius="16px"
              px="8px"
              py="4px"
              _hover={{ bg: 'neutrals.300' }}
              onClick={() => {
                handleAndCondition(getParentId(condition?.id), condition?.id);
              }}
            >
              <Typography variant="xsmall">{t("AND")}</Typography>
              <Icons.Plus cursor="pointer" height={"12px"} width={"12px"} />
            </Flex>
          </Box>
        </Flex>
      

      {(isOpen) && (
        <AndOrConditionBox
          conditionData={condition}
          conditionsMetaData={conditionsMetaData}
          handleDeleteCondition={onDeleteCondition}
          updateConditionData={updateConditionData}
          allCondition={allCondition}
          conditionName={conditionName}
          updateConditionName={updateConditionName}
        />
      )}
    </Box>
  );
};

export default ConditionSection;
